import { useRef, useState } from 'react';

import { ProjectThumb, useToast } from '@/components/ui';
import { useMetaContext } from '@/components/Layout';
import { uploadProjectIcon } from '@/api/projects';
import { getErrorMessage } from '@/api';

import styles from './ProjectIconEditor.module.css';

interface ProjectIconEditorProps {
  projectId: string | null;
  prefix: string;
  displayName: string | null;
  iconUrl: string | null;
  size: number;
  className?: string;
  // 업로드 성공 후 부모에서 project 재fetch.
  onUploaded?: () => void;
}

const MAX_BYTES = 5 * 1024 * 1024;

const readAsDataURL = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

// 헤더 썸네일 위에 hover 오버레이로 아이콘을 교체. admin + project_id 있을 때만 편집 가능.
// 파일 선택 즉시 업로드하고, 성공하면 방금 고른 이미지를 그대로 미리 보여준다.
export default function ProjectIconEditor({
  projectId,
  prefix,
  displayName,
  iconUrl,
  size,
  className,
  onUploaded,
}: ProjectIconEditorProps) {
  const meta = useMetaContext();
  const isAdmin = meta.current_user.platform_role === 'admin';
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const canEdit = isAdmin && !!projectId;
  const radius = Math.max(8, Math.floor(size * 0.16));

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !projectId) return;
    if (!file.type.startsWith('image/')) {
      toast.error('이미지 파일만 업로드할 수 있어요');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('이미지는 5MB 이하만 업로드할 수 있어요');
      return;
    }
    const dataUrl = await readAsDataURL(file);
    setPreview(dataUrl);
    setUploading(true);
    try {
      await uploadProjectIcon(projectId, dataUrl);
      toast.success('아이콘을 변경했어요');
      onUploaded?.();
    } catch (err) {
      setPreview(null);
      toast.error(getErrorMessage(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div
      className={`${styles.wrap} ${className ?? ''}`}
      style={{ width: size, height: size, borderRadius: radius }}
    >
      {preview ? (
        <img
          src={preview}
          width={size}
          height={size}
          alt="아이콘 미리보기"
          style={{ borderRadius: radius, objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <ProjectThumb prefix={prefix} display_name={displayName} iconUrl={iconUrl} size={size} />
      )}
      {canEdit && (
        <>
          <button
            type="button"
            className={styles.overlay}
            style={{ borderRadius: radius }}
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            aria-label="아이콘 이미지 변경"
          >
            {uploading ? (
              <span className={styles.spinner} aria-hidden />
            ) : (
              <span className={styles.label}>이미지 변경</span>
            )}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={onPick}
            style={{ display: 'none' }}
          />
        </>
      )}
    </div>
  );
}
